document.addEventListener('DOMContentLoaded', async () => {
    const overlayHideTimeouts = new WeakMap();
    const copyFeedbackTimeouts = new WeakMap();

    const copyToClipboard = async (value) => {
        if (typeof value !== 'string' || value.length === 0) {
            return false;
        }

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(value);
                return true;
            }
        } catch (error) {
            // fallback below
        }

        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);

        const selection = document.getSelection();
        const previousRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

        textarea.select();

        let copied = false;
        try {
            copied = document.execCommand('copy');
        } catch (error) {
            copied = false;
        }

        textarea.remove();

        if (previousRange && selection) {
            selection.removeAllRanges();
            selection.addRange(previousRange);
        }

        return copied;
    };

    const overlayElements = document.querySelectorAll('[data-overlay]');

    const isAnyOverlayVisible = () =>
        Array.from(overlayElements).some((overlay) => overlay.classList.contains('overlay--visible'));

    const lockScroll = () => {
        document.body.classList.add('noscroll');
        document.documentElement.classList.add('noscroll');
    };

    const unlockScrollIfNeeded = () => {
        if (!isAnyOverlayVisible()) {
            document.body.classList.remove('noscroll');
            document.documentElement.classList.remove('noscroll');
        }
    };

    const hideOverlay = (overlayElement) => {
        if (!overlayElement || overlayElement.classList.contains('hidden')) {
            return;
        }

        const container = overlayElement.querySelector('[data-overlay-container]');

        overlayElement.classList.remove('overlay--visible');
        overlayElement.setAttribute('aria-hidden', 'true');

        if (container) {
            container.classList.remove('overlay__container--visible');
        }

        const existingTimeout = overlayHideTimeouts.get(overlayElement);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
            overlayHideTimeouts.delete(overlayElement);
        }

        const timeoutId = window.setTimeout(() => {
            overlayElement.classList.add('hidden');
            overlayHideTimeouts.delete(overlayElement);
            unlockScrollIfNeeded();
        }, 350);
        overlayHideTimeouts.set(overlayElement, timeoutId);
    };

    overlayElements.forEach((overlayElement) => {
        const container = overlayElement.querySelector('[data-overlay-container]');
        const closeButton = overlayElement.querySelector('[data-overlay-close]');

        if (closeButton) {
            closeButton.addEventListener('click', () => hideOverlay(overlayElement));
        }

        overlayElement.addEventListener('click', (event) => {
            if (container && !container.contains(event.target)) {
                hideOverlay(overlayElement);
            }
        });
    });

    const showOverlay = (overlayElement) => {
        if (!overlayElement) {
            return;
        }

        const container = overlayElement.querySelector('[data-overlay-container]');

        const existingTimeout = overlayHideTimeouts.get(overlayElement);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
            overlayHideTimeouts.delete(overlayElement);
        }

        if (container) {
            container.classList.remove('overlay__container--visible');
        }

        overlayElement.classList.remove('hidden');
        lockScroll();

        requestAnimationFrame(() => {
            overlayElement.classList.add('overlay--visible');
            overlayElement.setAttribute('aria-hidden', 'false');

            if (container) {
                container.classList.add('overlay__container--visible');
            }
        });
    };

    const getVisibleOverlay = () =>
        Array.from(overlayElements).find((overlay) => overlay.classList.contains('overlay--visible'));

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') {
            return;
        }

        const activeOverlay = getVisibleOverlay();
        if (activeOverlay) {
            hideOverlay(activeOverlay);
        }
    });

    // if wp/core button has class js__overlay add data-open-overlay attribute to its <a> child
    document.querySelectorAll('.wp-block-button[class*="js__overlay-"]').forEach((el) => {
        const link = el.querySelector('a');
        if (!link) return;
        el.classList.forEach((cls) => {
            if (cls.startsWith('js__overlay-')) {
                link.setAttribute('data-open-overlay', cls.replace('js__overlay-', ''));
            }
        });
    });

    // handle copy-to-clipboard triggers inside overlays
    document.addEventListener('click', async (event) => {
        const copyButton = event.target.closest('[data-copy-overlay]');
        if (!copyButton) {
            return;
        }

        const copyValue = copyButton.getAttribute('data-copy-overlay');
        if (!copyValue) {
            return;
        }

        const wrapper = copyButton.closest('[data-copy-wrapper]');
        const feedback = wrapper ? wrapper.querySelector('[data-copy-feedback]') : null;

        const successMessage = copyButton.getAttribute('data-copy-success') || 'Link kopiert!';
        const errorMessage =
            copyButton.getAttribute('data-copy-error') || 'Kopieren nicht möglich. Bitte manuell kopieren.';

        const showFeedback = (message) => {
            if (!feedback) {
                return;
            }

            feedback.textContent = message;
            feedback.classList.remove('hidden');

            const existingTimeout = copyFeedbackTimeouts.get(feedback);
            if (existingTimeout) {
                clearTimeout(existingTimeout);
            }

            const timeoutId = window.setTimeout(() => {
                feedback.classList.add('hidden');
                copyFeedbackTimeouts.delete(feedback);
            }, 2500);

            copyFeedbackTimeouts.set(feedback, timeoutId);
        };

        const copied = await copyToClipboard(copyValue);
        showFeedback(copied ? successMessage : errorMessage);
    });

    // to add an overlay use the overlay component with prop overlayContent = name of partial that should be displayed inside the overlay
    // for the opening trigger add data-open-overlay attr to the button/element with the same partial as value
    document.addEventListener('click', (event) => {
        const openButton = event.target.closest('[data-open-overlay]');
        if (!openButton) return;

        const overlayName = openButton.getAttribute('data-open-overlay');

        const openOverlay = document.querySelector(`[data-overlay="${overlayName}"]`);

        if (openOverlay) {
            const headingTarget = openOverlay.querySelector('[data-overlay-heading-target]');
            if (headingTarget) {
                const customHeading = openButton.getAttribute('data-overlay-heading');
                if (customHeading) {
                    headingTarget.textContent = customHeading;
                } else {
                    const fallbackText = (openButton.textContent || '')
                        .split(/\s+/)
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ')
                        .trim();

                    headingTarget.textContent = fallbackText || headingTarget.textContent;
                }
            }

            showOverlay(openOverlay);
        }
    });
});
